import { browser } from '$app/environment';
import { v4 as uuidv4 } from 'uuid';
import { COOKIES, LOCAL_STORAGE_KEYS, QUERY_PARAM_KEYS } from './constants';
import * as Sentry from '@sentry/sveltekit';
import Cookies from 'js-cookie';
import { AnalyticsService } from '$lib/services/analytics/index.js';
import { LocalStorageStore } from './local-storage-store';

let deviceIdFallbackUsed = false;

// Deferred event queue mechanism (scrappy, local solution)
// TODO: remove this once we have refactored the device id logic away from cookies - AD
let isDeferralActive = false;
const deferredEventsQueue = [];

/**
 * Wrapper for AnalyticsService.statelessEventLog that queues events when deferral is active.
 * @param {object} payload - The payload to log
 * @param {string} deviceId - The device id to log
 * @param {boolean} preserveSessionDeviceId - Whether to preserve the session device id
 * @returns {Promise<object>} - A promise that resolves when the event is logged (or queued)
 */
const deferredStatelessEventLog = async (payload, deviceId, preserveSessionDeviceId) => {
	if (isDeferralActive) {
		deferredEventsQueue.push(() =>
			AnalyticsService.statelessEventLog(payload, deviceId, preserveSessionDeviceId)
		);
		return Promise.resolve();
	} else {
		return AnalyticsService.statelessEventLog(payload, deviceId, preserveSessionDeviceId);
	}
};

/**
 * Enables event deferral - events will be queued instead of firing immediately.
 * @returns {void}
 */
export const enableEventDeferral = () => {
	isDeferralActive = true;
};

/**
 * Disables event deferral - events will fire immediately.
 * @returns {void}
 */
export const disableEventDeferral = () => {
	isDeferralActive = false;
};

/**
 * Flushes all deferred events, executing them in order.
 * @returns {Promise<void>}
 */
export const flushDeferredEvents = async () => {
	const events = [...deferredEventsQueue];
	deferredEventsQueue.length = 0;

	for (const eventFn of events) {
		try {
			await eventFn();
		} catch (err) {
			Sentry.captureException(err, {
				extra: {
					action: 'Error flushing deferred device ID event'
				}
			});
		}
	}
};

export const localStorageStore = new LocalStorageStore();

export const migrateDeviceIdAndFixErroneousValues = () => {
	const cookiedDeviceId = Cookies.get(COOKIES.DEVICE_ID);
	const localStorageDeviceId = localStorageStore.get(LOCAL_STORAGE_KEYS.DEVICE_ID);

	if (cookiedDeviceId === 'null') {
		Sentry.addBreadcrumb({
			category: 'device_id_migration',
			message: 'Removing erroneous (null) device ID from cookies'
		});
		Cookies.remove(COOKIES.DEVICE_ID);
	}

	if (localStorageDeviceId === 'null') {
		Sentry.addBreadcrumb({
			category: 'device_id_migration',
			message: 'Removing erroneous (null) device ID from localStorage'
		});
		localStorage.removeItem(LOCAL_STORAGE_KEYS.DEVICE_ID);
	} else if (localStorageDeviceId && localStorageDeviceId !== 'null') {
		Sentry.addBreadcrumb({
			category: 'device_id_migration',
			message: 'Migrating (non-null) device ID from prior storage to new storage'
		});
		saveDeviceId(localStorageDeviceId);
	}
};

export const DEVICE_ID_FAILURE_TYPES = {
	fallback: 'device_id_fallback',
	init_failure: 'device_id_init_failure',
	get_failure: 'device_id_get_failure'
};

/**
 * Fires a device ID failure event.
 * @param {string} failureType - The type of failure that occurred
 * @param {string} backupDeviceId - The backup device ID to fire the event for
 * @returns {void} The device ID failure event, or null if not initialized
 */
export const fireDeviceIdFailureEvent = (
	failureType,
	backupDeviceId = 'client_failure:fireDeviceIdFailureEvent:default'
) => {
	switch (failureType) {
		case DEVICE_ID_FAILURE_TYPES.fallback:
			if (deviceIdFallbackUsed) return;
			else {
				deviceIdFallbackUsed = true;
				deferredStatelessEventLog(
					{
						event_subtype: DEVICE_ID_FAILURE_TYPES.fallback,
						browser,
						window: typeof window !== 'undefined'
					},
					backupDeviceId
				).catch((err) =>
					Sentry.captureException(err, {
						extra: {
							action: 'getDeviceId fallback error'
						}
					})
				);
			}
			break;
		default:
			deferredStatelessEventLog(
				{
					event_subtype: failureType,
					browser,
					window: typeof window !== 'undefined'
				},
				backupDeviceId
			);
			break;
	}
};

/**
 * Gets an existing device ID from cookies.
 * Supports both browser (uses js-cookie).
 * @returns {string | null} The existing device ID, or null if not found
 */
export const getDeviceId = () => {
	if (typeof window !== 'undefined') {
		let determinedDeviceId = null;
		const browserCookiedDeviceId = Cookies.get(COOKIES.DEVICE_ID);
		const localStorageDeviceIdBackUp = localStorageStore?.get?.(
			LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP
		);

		if (browserCookiedDeviceId) {
			determinedDeviceId = browserCookiedDeviceId;
		} else if (localStorageDeviceIdBackUp) {
			determinedDeviceId = localStorageDeviceIdBackUp;
			fireDeviceIdFailureEvent(DEVICE_ID_FAILURE_TYPES.fallback, localStorageDeviceIdBackUp);
		}

		return determinedDeviceId;
	} else return null;
};

/**
 * Sets Sentry tags and user context for a device ID.
 * This should be called after the device ID is saved to cookies.
 * @param {string} deviceId - The device ID to set in Sentry
 */
const setSentryDeviceId = (deviceId) => {
	Sentry.setTag(COOKIES.DEVICE_ID, deviceId);
	Sentry.setUser({ id: deviceId });
};

/**
 * Creates a new device ID and saves it to cookies.
 * Also sets Sentry tags and user context.
 * @returns {boolean} whether the device ID was saved successfully
 */
const createAndSaveDeviceId = () => {
	const deviceId = uuidv4();
	return saveDeviceId(deviceId);
};

/**
 * Saves a device ID to cookies.
 * Supports only browser (uses js-cookie).
 * Also sets Sentry tags and user context after saving.
 * @param {string} deviceId - The device ID to save
 * @returns {boolean} whether the device ID was saved successfully
 */
export const saveDeviceId = (deviceId) => {
	console.assert(deviceId, 'Device ID is either not required or not truthy');
	if (!deviceId) return false;

	Cookies.set(COOKIES.DEVICE_ID, deviceId, COOKIES.DEVICE_ID_CONFIG);

	localStorageStore.set(LOCAL_STORAGE_KEYS.DEVICE_ID_BACKUP, deviceId);

	setSentryDeviceId(deviceId);
	return getDeviceId() === deviceId;
};

/**
 * Initializes the device ID once per web app load.
 * This should be called early in the app lifecycle (e.g., in +layout.js).
 * Behavior:
 * - Checks URL params first (if found, saves it to cookies)
 * - Checks cookies for existing device ID
 * - Creates and saves a new device ID if none exists
 * Use getDeviceId() to read the device ID after initialization.
 * @returns {Promise<boolean>} True if the device ID was initialized as defined above, false if not
 */
export const initializeDeviceId = async () => {
	if (window === undefined) return false;

	const existingDeviceId = getDeviceId();
	const searchParams = new URLSearchParams(window.location.search);

	if (searchParams.has(QUERY_PARAM_KEYS.DEVICE_ID)) {
		const deviceIdFromUrl = searchParams.get(QUERY_PARAM_KEYS.DEVICE_ID) || '';

		if (deviceIdFromUrl && deviceIdFromUrl !== 'null' && deviceIdFromUrl !== existingDeviceId) {
			await deferredStatelessEventLog(
				{
					event_subtype: 'device_id_update',
					source: 'url_param',
					initial_device_id: existingDeviceId,
					new_device_id: deviceIdFromUrl
				},
				deviceIdFromUrl,
				false
			);
			saveDeviceId(deviceIdFromUrl);
			return true;
		}
	}

	if (existingDeviceId) {
		setSentryDeviceId(existingDeviceId);
		return true;
	}

	createAndSaveDeviceId();
	return true;
};
